import { createApp } from 'vue';
import App from './App.vue';
import store from './store';
import { createHead } from '@vueuse/head';
import './assets/js/gsap.js';

// ─────────────────────────────────────────────────
// If you're reading this, we should talk.
// ─────────────────────────────────────────────────
console.log(
  `%c
     ╷
     │  Jacques Ramphal
     │  Design systems, production code, agentic tooling.
     │
     │  You're inspecting the source — that's my favourite
     │  kind of reader. This site is the work sample.
     │
     │  → ramphal.design/hire
     │  → mailto:jacques@ramphal.design
     ╵
`,
  'font-family: monospace; font-size: 12px; line-height: 1.6; color: inherit;'
);

import { registerGlobalComponents } from './globalComponents';
import { syncLineWidth } from './utils/lineWidth';

syncLineWidth();

import router from './router';
import { Directive, DirectiveBinding, VNode } from 'vue';
import { triggerHaptic } from 'tactus';

const isTouchDevice = () => navigator.maxTouchPoints > 0;

// Global haptic feedback — mobile only, respects user toggle
document.addEventListener('click', (e) => {
  if (!isTouchDevice()) return;
  if (localStorage.getItem('user-haptic') === 'off') return;
  const target = (e.target as HTMLElement).closest('a, button');
  if (
    target &&
    !target.hasAttribute('disabled') &&
    target.getAttribute('aria-disabled') !== 'true'
  ) {
    triggerHaptic();
  }
});

// Global sound feedback — respects user toggle
const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
let audioCtx: AudioContext | null = null;

const playClickSound = () => {
  try {
    if (!AudioCtx) return;
    if (!audioCtx) audioCtx = new AudioCtx();
    if (audioCtx.state === 'suspended') audioCtx.resume();
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    osc.connect(gain);
    gain.connect(audioCtx.destination);
    osc.type = 'sine';
    osc.frequency.value = 600;
    gain.gain.setValueAtTime(0.04, audioCtx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.08);
    osc.start();
    osc.stop(audioCtx.currentTime + 0.08);
  } catch (e) {
    // ignore AudioContext errors
  }
};

document.addEventListener('click', (e) => {
  if (localStorage.getItem('user-sound') !== 'on') return;
  const target = (e.target as HTMLElement).closest('a, button');
  if (
    target &&
    !target.hasAttribute('disabled') &&
    target.getAttribute('aria-disabled') !== 'true'
  ) {
    playClickSound();
  }
});

// highlight.js is loaded lazily the first time the directive actually runs, so
// its ~35-language "common" build (a big dependency) stays out of the initial
// bundle entirely. Pages without code blocks — the home page included — never
// download it. The import is cached after the first call.
let hljsPromise: Promise<typeof import('highlight.js/lib/common').default> | null = null;
const loadHljs = () => {
  if (!hljsPromise) {
    hljsPromise = import('highlight.js/lib/common').then((m) => m.default);
  }
  return hljsPromise;
};

// Define the custom directive
const highlightjsDirective = {
  deep: true,
  beforeMount: function (el, binding) {
    // on first bind, highlight all targets
    const targets = el.querySelectorAll('code');
    if (!targets.length) return;
    loadHljs().then((hljs) => {
      targets.forEach((target) => {
        // if a value is directly assigned to the directive, use this
        // instead of the element content.
        if (binding.value) {
          target.textContent = binding.value;
        }
        hljs.highlightElement(target);
      });
    });
  },
  updated: function (el, binding) {
    // after an update, re-fill the content and then highlight
    const targets = el.querySelectorAll('code');
    if (!targets.length || !binding.value) return;
    loadHljs().then((hljs) => {
      targets.forEach((target) => {
        target.textContent = binding.value;
        hljs.highlightElement(target);
      });
    });
  },
};

export const appear: Directive = {
  beforeMount(element: HTMLElement) {
    element.style.visibility = 'hidden';
  },
  updated(element: HTMLElement, binding: DirectiveBinding<boolean>, node: VNode) {
    if (!binding.value === !binding.oldValue || null === node.transition) {
      return;
    }
    if (!binding.value) {
      node.transition.leave(element, () => {
        element.style.visibility = 'hidden';
      });
      return;
    }
    node.transition.beforeEnter(element);
    element.style.visibility = '';
    node.transition.enter(element);
  },
};

// Create the Vue app instance
const app = createApp(App);
const head = createHead();

// Use the custom directives
app.directive('appear', appear);
app.directive('highlightjs', highlightjsDirective);

// Use plugins, components, and mount the app as before
app.use(router); // Use Vue Router plugin
app.use(store); // Use Vuex store plugin
app.use(head); // Use VueUse Head for dynamic meta tags

// Global Components (shared with Storybook)
registerGlobalComponents(app);

// Mount the app
app.mount('#app');
